import React, { useCallback, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { CloseIcon } from '@icons/CloseIcon'
import { DummyIcon } from '@icons/DummyIcon'

import { Heading6 } from '@ui/Typography'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { wait } from '@utils/wait'

import { FreeTextAnswer, IBTestSubCategory } from '@model/TestSectionsDto'

import { Loading } from '@global/Loading/Loading'
import IconButton from '@global/iconButton/IconButton'

import { useFetchOne } from '@hooks/useFetch'
import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { IBCarousel } from '../Carousel/IBCarousel'
import { IBQuestionnaire } from './IBQuestionnaire'

import styles from './IBQuestionnairePage.module.scss'

export const IBQuestionnairePage: React.FC = () => {
  const { t } = useTranslation()
  const { subCategoryId } = useParams<{ subCategoryId: IBTestSubCategory }>()
  const { account } = useAccountReadContext()
  const { refreshAccount } = useAccountWriteContext()
  const { scrollIntoView } = useScrollToElementIds()
  const [isLoading, setIsLoading] = useState(false)

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const navigate = useNavigate()

  const ibSignUpTestCallback = useCallback(async () => {
    if (subCategoryId) {
      return apiClient.getIBSignUpTest(locale, subCategoryId)
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, subCategoryId])

  const { data: ibSignUpTest, isLoading: isLoadingIBSignUpTest } = useFetchOne(ibSignUpTestCallback)

  const legalDocumentsCallback = useCallback(async () => {
    const ibScheme =
      subCategoryId === IBTestSubCategory.Default
        ? '0'
        : subCategoryId === IBTestSubCategory.MultiTier
        ? '1'
        : ''
    return apiClient.getIntroducingBrokerLegalDocuments(
      locale,
      ibScheme ? `&IbSchemes=${ibScheme}` : ''
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, subCategoryId])

  const { data: legalDocuments = [], isLoading: isLoadingLegalDocuments } =
    useFetchOne(legalDocumentsCallback)

  const handleSignUpSubmit = async (selectedAnswers: string[], freeAnswers: FreeTextAnswer[]) => {
    try {
      setIsLoading(true)
      scrollIntoView([ScrollToIds.IBSignupHeader, ScrollToIds.IBProfileHeader])
      if (!account || !ibSignUpTest?.sections[0].questions) {
        return
      }

      await apiClient.signUpIntroducingBroker({
        category: 'IB information',
        clientId: account?.id,
        testId: ibSignUpTest?.id,
        dateTaken: new Date(),
        selectedAnswers,
        freeAnswers,
      })

      await wait()
      await refreshAccount(locale)
      navigate('../thank-you')
    } catch (e: ResponseError | any) {
      const error = e as ResponseError
      console.error(error.response.response)
    } finally {
      setIsLoading(false)
    }
  }

  useLayoutEffect(() => {
    scrollIntoView([ScrollToIds.IBProfileHeader])
  }, [isLoadingLegalDocuments, scrollIntoView])

  return (
    <div className={styles.questionnaire}>
      <div className={styles.titleWrapper}>
        <IconButton
          onClick={() => {
            navigate('../../type')
            scrollIntoView([ScrollToIds.IBSignupHeader, ScrollToIds.IBProfileHeader])
          }}
        >
          <CloseIcon />
        </IconButton>
        <Heading6 className={styles.title} medium>
          {subCategoryId === IBTestSubCategory.MultiTier
            ? t('IB.Multitier Program')
            : t('IB.Introducing Broker Program')}
        </Heading6>
        <DummyIcon />
      </div>
      <Loading
        isLoading={isLoadingIBSignUpTest || isLoadingLegalDocuments || isLoading}
        showLoadingIcon
      >
        <div className={styles.wrapper}>
          {!!ibSignUpTest && (
            <div className={styles.formInputWrapper}>
              <IBQuestionnaire
                legalDocuments={legalDocuments}
                testData={ibSignUpTest}
                onSubmit={handleSignUpSubmit}
                isMultiTier={subCategoryId === IBTestSubCategory.MultiTier}
              />
            </div>
          )}
          <IBCarousel subCategoryId={subCategoryId} />
        </div>
      </Loading>
    </div>
  )
}
